import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Avatar, Chip, Divider, Grid2 as Grid, Link, Typography, Stack, FormControl, OutlinedInput, IconButton, InputAdornment, InputLabel, Select, MenuItem, TableContainer, Table, TableHead, TableRow, TableCell, Paper, TableBody, Drawer, Box, useMediaQuery, debounce, CircularProgress, Button} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Main from '../../../layouts/Main';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { green } from '@mui/material/colors';
import {Search, WatchLater } from '@mui/icons-material';

import ReportLog from '../../../types/reports/reportLog';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import DisplayReport from '../../Report/DisplayReport';

import { getReportTotalAmount } from '../../../utility/getReportTotalAmount';
import { getReportColorDisplay } from '../../../utility/getReportColorDisplay';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const ReportTypeDropdown = [
    {
        value: '1',
        label: 'Campaign Finance Reports',
    },
    {
        value: 'D',
        label: 'Dissolution Reports',
    },
    {
        value: '61',
        label: 'Late Contribution Reports',
    },
    {
        value: 'Q',
        label: 'Ballot Question Reports',
    },
    {
        value: '200',
        label: 'Independent Expenditures',
    },
    {
        value: '202',
        label: 'Electioneering Communications',
    },
    {
        value: '203',
        label: 'Ballot Question CPF22s',
    }
]


//to be removed later
const PageMain = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
  }>(({ theme }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: 0,
    position: 'relative',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginRight: theme.breakpoints.down('sm')? '65%': '100%',
        },
      },
    ],
  }));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

const Log = (): JSX.Element => {

    document.title = 'OCPF - Recently Filed Reports';

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const drawerWidth = isSM? '100%': '65%'; 
    const [isLoading, setIsLoading] = useState(true);
    
    const [reportsLog, setReportsLog] = useState<ReportLog[]>();
    const [isReportOpen, setReportOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState<ReportLog>();

    const [reportType, setReportType] = useState<string>('1');
    const [searchInput, setSearchInput] = useState<string>('');
    const [isIE, setIsIE] = useState(false);
    const [pdfTypeLink, setPdfTypeLink] = useState<string>('report');
    
    const [pageSize, setPageSize] = useState(50);

    useEffect(() => {
        getReportLog();
      }, [reportType, searchInput, pageSize]);


    const openReportDrawer = (report:ReportLog) => {
        setSelectedReport(report);
        setReportOpen(true);
    };
  
    const closeReportDrawer = () => {
      setReportOpen(false);
    };

    const openFilerPage = (cpfId: number, event: any) => {
        event.stopPropagation();
        window.location.href = `/filers?q=${cpfId}`;
    }

    const getReportLog = async () => {
    
        //customzie the api call based on the report type
        let apiCallType ='reports'
        let query = "";
        setIsIE(false);
        setPdfTypeLink('report');

        //revisit after the API changes
        if(reportType != "1"){
            let reportTypeId  = reportType === '61' ? '61': '';
            query = reportType === '61' ? `&reportTypeId=${reportTypeId}` : `&reportTypeCategory=${reportType}`;
        }

        if(reportType === '200' || reportType === '202' || reportType === '203'){
            apiCallType = 'miscreports';
            query = `&reportTypeId=${reportType}`;
            setIsIE(true);
            setPdfTypeLink('miscreport');
        }
        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/${apiCallType}/log?name=${searchInput}${query}&pageSize=${pageSize}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get the latest filed reports.'));
            }
            return result.json();
          })
          .then(result => {
            setIsLoading(false);
            setReportsLog(result);
          });
        }catch(error){
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
    }

    const updateReportType = (event: any) => {
        setReportType(event.target.value);
    }

    const filterByName = debounce((event: any) => {
        let searchInput = event.target.value.toLowerCase();
        setSearchInput(searchInput);
      }, 500);

    const loadMoreReports = () => {
        setPageSize(pageSize + 50);
    }

  return (
    <Main>
      <Box
        maxWidth={'1200px'}
        width={1}
        margin={'0 auto'}
        paddingX={2}
        paddingY={{ xs: 4, sm: 6, md: 6 }}
       >
        <PageBreadCrumbs></PageBreadCrumbs>
        <Stack  data-aos={'fade-down'} direction={"row"} spacing={1} marginBottom={2} marginTop={3}>
            <Avatar variant="rounded" 
                sx={{ 
                    bgcolor: green[500],
                    width: 36, 
                    height: 36
                }}
            >
                <AssessmentIcon />
            </Avatar>
            <Typography
                variant="h5"
                align={'left'}
                gutterBottom
                sx={{
                    fontWeight: 700,
                }}
                paddingTop={0.5}
                marginBottom={2}
            >
            Recently Filed Reports
        </Typography>
        </Stack>
        <Divider/>
        <>
            <Grid container marginBottom={2} marginTop={2}>
                <Grid size={{ xs: 12, md: 10 }}>
                    <Stack direction={"row"} spacing={1} sx={{ mb: 1}}>
                        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                            <InputLabel id="type-id">Type</InputLabel>
                            <Select 
                                labelId="type-id"
                                id="type-id"
                                aria-labelledby='type-id'
                                label="Report Type"
                                value={reportType}
                                onChange={updateReportType}
                            >
                            {
                                ReportTypeDropdown.map((type) =>
                                <MenuItem value={type.value} key={type.value} >{type.label}</MenuItem>
                                )
                            }
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ m: 1, p:0, maxWidth: 200 }}>
                            <OutlinedInput
                                aria-label='search filer name'
                                placeholder={"Search filer name..."}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                }
                                onChange={filterByName}
                            />
                        </FormControl>
                    </Stack>
                </Grid>
             </Grid>
             <Grid container spacing={1}>
                 <TableContainer component={Paper} sx={{ width: "100%"}}>
                    <Table aria-label="report log table">
                        <TableHead>
                            <TableRow>
                                {
                                    !isSM &&
                                    <TableCell width={'20%'}>
                                        <Typography variant={'subtitle2'} fontWeight={600}>
                                        Filed On
                                        </Typography>
                                    </TableCell>
                                }
                                 <TableCell width={'30%'}>
                                     <Typography variant={'subtitle2'} fontWeight={600}>
                                     Filer
                                     </Typography>
                                 </TableCell>
                                 <TableCell>
                                     <Typography variant={'subtitle2'} fontWeight={600}>
                                      Report
                                     </Typography>
                                 </TableCell>
                                 <TableCell>
                                     <Typography variant={'subtitle2'} fontWeight={600}>
                                     Total Amount
                                     </Typography>
                                 </TableCell>
                                 {
                                    isSM &&
                                    <TableCell>
                                        <Typography variant={'subtitle2'} fontWeight={600}>
                                        Filed On
                                        </Typography>
                                    </TableCell>
                                 }
                            </TableRow>
                         </TableHead>
                         <TableBody>
                            {
                              reportsLog?.map((report) =>
                                <TableRow 
                                    key={report.reportId}
                                    sx={{ 
                                        '&:last-child td, &:last-child th': { border: 0 }, 
                                        "&:hover": {
                                          boxShadow: "0 0 10px #ccc",
                                        },
                                      }}
                                      className="is-clickable"
                                      onClick={()=> openReportDrawer(report)}   
                                >
                                    {
                                        !isSM &&
                                        <TableCell width={'20%'}>
                                            <Chip icon={<WatchLater />} label={`${report.dateFiled}`} color="success" variant="outlined" />
                                        </TableCell>
                                    }
                                     <TableCell sx={{ textAlign: "left!important"}} width={'30%'}>
                                        <Typography variant="body2" className="is-clickable">
                                            {
                                                report.cpfId>0 &&
                                                <Link marginRight={1} onClick={(event: any)=>openFilerPage(report.cpfId, event)} underline="none">{report.cpfId}</Link>
                                            }
                                            <b>{report.fullNameReverse}</b>
                                        </Typography>
                                        <Typography component={"i"} variant={'subtitle2'}>
                                           {report.officeSought}
                                        </Typography>
                                    </TableCell>
                                    <TableCell width={'25%'}>
                                         <Typography variant={'subtitle2'}>
                                            <Link color={getReportColorDisplay(report.reportTypeId)} 
                                                fontWeight={550} 
                                                target="_blank" 
                                                underline="none"
                                            >
                                                {report.reportTypeDescription} {report.amendmentDisplay.length > 0 && "Amendment"}
                                            </Link>
                                         </Typography>
                                         <Typography color={'text.secondary'} component={'i'} variant={'subtitle2'}>
                                             {report.reportingPeriod}
                                         </Typography>
                                     </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {getReportTotalAmount(report)}
                                         </Stack>
                                    </TableCell>
                                    {
                                        isSM &&
                                        <TableCell>
                                            <Typography variant={'subtitle2'}>
                                                {report.dateFiled}
                                            </Typography>
                                        </TableCell>
                                    }
                                </TableRow>
                              )
                            }
                             <TableRow>
                                <TableCell colSpan={4} align={isSM?"left":"right"}>
                                    <Button size="small" variant="outlined" onClick={loadMoreReports}>LOAD MORE REPORTS</Button>
                                </TableCell>
                            </TableRow>
                         </TableBody>
                    </Table>
                </TableContainer>
             </Grid>
            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                    width: drawerWidth,
                },
                }}
                    //variant="persistent"
                    anchor="right"
                    open={isReportOpen}
                    onClose={closeReportDrawer}
                >
                <DrawerHeader>
                    <Grid container width={1}>
                        <Grid size={{ xs: 12, md: 8 }} display={"flex"} justifyContent={"flex-start"}>
                             <Stack direction="row" spacing={1} marginTop={1}>
                                <IconButton onClick={closeReportDrawer}>
                                    {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                                </IconButton>
                                {
                                    selectedReport &&
                                    <Typography variant='h6' display={"flex"} fontWeight={550} alignItems={"center"}>
                                        {selectedReport.reportTypeDescription} {selectedReport.reportingPeriod}
                                    </Typography>
                                }
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }} display={"flex"} justifyContent={isSM?"flex-start":"flex-end"}>
                            <Button component={Link} href={`${process.env.REACT_APP_API_BASE_URL}/${pdfTypeLink}/pdf/${selectedReport?.reportId}`} target='_blank' underline="none" variant="text" startIcon={<PictureAsPdfIcon />} color="success">Export to PDF</Button>
                        </Grid>
                    </Grid>
                </DrawerHeader>
                <Divider textAlign="right">
                    <Typography variant='body2' display={"flex"} alignItems={"center"}>{selectedReport?.reportId}</Typography>
                </Divider>
                {
                    selectedReport &&
                    <DisplayReport report={selectedReport} isIE={isIE}></DisplayReport>
                }
            </Drawer> 
        </>
     </Box>
      {/*loading indicator*/}
        <CircularProgress
            aria-label='loading'
            size={50}
            className={isLoading? "": "hidden"}
            sx={{
                position: "fixed",
                left: "50%",
                top: "20%",
                transform: "translate(-50%, -50%)",
                zIndex: 2
            }}
        />
    </Main>
  );
};

export default Log;
